import { useRef } from 'react';
import Router from 'next/router';
import api from 'api';
import { useDispatch, useReduxState } from 'hooks';
import { actions } from 'store';
import { ModalButtonWithForm } from 'components/common';
import { FormGroup, Text } from 'components/common/form';
import type { CopyCourseRes } from 'shared-types/responses';

interface Props {
  courseId: number;
  courseTitle: string;
  isDropdownButton?: boolean;
}

const CoursesTileCopy = ({
  courseId,
  courseTitle,
  isDropdownButton
}: Props) => {
  const { dispatch } = useDispatch();
  const { messages, theme } = useReduxState();

  const titleInputContainer = useRef<HTMLDivElement>(undefined);

  const handleClick = (ev) => {
    ev.stopPropagation();
  };

  const handleModalVisible = () => {
    titleInputContainer.current.querySelector('input').focus();
  };

  const handleSubmit = async (values) => {
    const response = await api.post<CopyCourseRes>(
      `/admin/courses/${courseId}/copy`,
      values
    );
    const newCourse = response.data.course;
    dispatch(
      actions.addNotice({
        text: messages.courseCopyCourseSuccess,
        type: 'success'
      })
    );
    Router.push(
      '/admin/courses/[courseSlug]',
      `/admin/courses/${newCourse.slug}`
    );
  };

  const validateTitle = async (value) => {
    if (!value) return messages.coursesTitleEmpty;

    try {
      await api.head(`/admin/courses?title=${value}`);
      return messages.coursesTitleInUse;
    } catch (err) {
      return undefined;
    }
  };

  return (
    <div
      className={isDropdownButton ? 'copy-dropdown' : 'copy-button'}
      onClick={handleClick}
      {...(isDropdownButton
        ? {}
        : { 'data-tooltip': messages.courseCopyCourse })}
    >
      <ModalButtonWithForm
        buttonCategory="light"
        buttonIcon={['far', 'copy']}
        buttonIsDropdown={isDropdownButton}
        buttonStyle={isDropdownButton ? {} : { borderRadius: 10 }}
        buttonText={isDropdownButton ? messages.courseCopyCourse : null}
        modalActionCategory="success"
        modalActionText={messages.courseCreateCopy}
        modalTitle={messages.courseCopyCourse}
        onModalVisible={handleModalVisible}
        onSubmit={handleSubmit}
        pristineIsValid={false}
      >
        {(formState) => (
          <div>
            <div>{`${messages.courseCreateCopyOf}: ${courseTitle}`}</div>
            <FormGroup
              htmlFor="copy-title"
              label={messages.courseNewCourseTitle}
            >
              <Text
                customValidateAsync={validateTitle}
                field="title"
                id="copy-title"
                inputContainerRef={(el) => (titleInputContainer.current = el)}
              />
            </FormGroup>
          </div>
        )}
      </ModalButtonWithForm>

      <style jsx>{`
        .copy-button :global(button) {
          background-color: ${theme.lightGrey};
          border-radius: 5px;
          color: #5f5f5f;
          height: 30px;
          padding: 6px 0;
          width: 30px;
        }
        .copy-button :global(button:hover) {
          background-color: ${theme.lightGreyDark};
        }
      `}</style>
    </div>
  );
};

export default CoursesTileCopy;
